import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useServerApi } from "../hooks/userServerApi";
import {
  formatApprovedFundListLabel,
  loadEffectiveApprovedFunds,
} from "../helpers/orderArFormFundUtils";

const ApprovedFundListContext = createContext({
  funds: [],
  fundListName: "",
  loading: true,
  error: null,
});

export const ApprovedFundListProvider = ({ children }) => {
  const [api] = useServerApi();
  const [funds, setFunds] = useState([]);
  const [fundListName, setFundListName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadEffectiveApprovedFunds(api);
        if (cancelled) return;
        setFunds(data?.funds ?? []);
        setFundListName(formatApprovedFundListLabel(data?.approvalFundList));
      } catch (err) {
        if (!cancelled) {
          setFunds([]);
          setFundListName("");
          setError(err?.message ?? "Failed to load approved fund list");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ funds, fundListName, loading, error }),
    [funds, fundListName, loading, error]
  );

  return (
    <ApprovedFundListContext.Provider value={value}>
      {children}
    </ApprovedFundListContext.Provider>
  );
};

export const useApprovedFundList = () => useContext(ApprovedFundListContext);
